import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Student Certificates View
 * - View all earned certificates
 * - Preview certificate in modal
 * - Download certificate as PDF/HTML
 */
const StudentCertificates = ({ studentId }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  useEffect(() => {
    if (!studentId) return;
    const loadCertificates = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/certificates/student/${studentId}`);
        setCertificates(res.data?.certificates || res.data || []);
      } catch (err) {
        console.error('Error loading certificates:', err);
      } finally {
        setLoading(false);
      }
    };
    loadCertificates();
  }, [studentId]);

  const handleView = (cert) => {
    const url = cert.view_url || `/api/certificates/${cert.certificate_id}/view`;
    setPreviewUrl(url);
    setPreviewTitle(cert.course_name || 'Certificate');
  };

  const handleDownload = async (cert) => {
    const url = cert.download_url || `/api/certificates/${cert.certificate_id}/download`;
    try {
      const res = await fetch(url, {
        headers: { 'Accept': 'application/octet-stream' }
      });
      if (!res.ok) {
        console.error('Download failed:', res.status);
        return;
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `certificate_${cert.certificate_number || cert.certificate_id}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading certificates...</div>;
  }

  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, color: '#1f2937' }}>🏆 My Certificates</h2>
        <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '14px' }}>
          View and download certificates for completed courses
        </p>
      </div>

      {certificates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: '#f9fafb', borderRadius: '16px', border: '2px dashed #d1d5db' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎓</div>
          <h3 style={{ color: '#374151', marginBottom: '8px' }}>No Certificates Yet</h3>
          <p style={{ color: '#6b7280', maxWidth: '400px', margin: '0 auto' }}>
            Complete your enrolled courses to earn certificates. Certificates are generated by your faculty after course completion.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '20px' }}>
          {certificates.map(cert => (
            <div key={cert.certificate_id} style={{
              background: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb',
              overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
            >
              {/* Certificate Top Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #1e40af, #7c3aed)',
                padding: '24px 20px', textAlign: 'center', position: 'relative'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>📜</div>
                <div style={{ color: '#fff', fontSize: '18px', fontWeight: 700 }}>Certificate of Completion</div>
                {cert.certificate_number && (
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginTop: '4px' }}>
                    #{cert.certificate_number}
                  </div>
                )}
              </div>

              {/* Certificate Details */}
              <div style={{ padding: '20px' }}>
                <h3 style={{ margin: '0 0 8px', color: '#1f2937', fontSize: '16px' }}>
                  {cert.course_name || 'Course'}
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                  {cert.program_name && (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      <span style={{ fontWeight: 600 }}>Program:</span> {cert.program_name}
                    </div>
                  )}
                  {cert.grade && (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      <span style={{ fontWeight: 600 }}>Grade:</span>{' '}
                      <span style={{ color: '#059669', fontWeight: 700 }}>{cert.grade}</span>
                    </div>
                  )}
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    <span style={{ fontWeight: 600 }}>Issued:</span>{' '}
                    {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    }) : 'N/A'}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleView(cert)}
                    style={{
                      flex: 1, padding: '10px', background: '#eff6ff', color: '#2563eb',
                      border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 600
                    }}
                  >
                    👁 View
                  </button>
                  <button
                    onClick={() => handleDownload(cert)}
                    style={{
                      flex: 1, padding: '10px', background: '#f0fdf4', color: '#059669',
                      border: '1px solid #bbf7d0', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 600
                    }}
                  >
                    ⬇ Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
          onClick={() => setPreviewUrl(null)}
        >
          <div style={{
            background: '#fff', borderRadius: '16px', width: '90%', maxWidth: '900px',
            height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 20px', borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>📜 {previewTitle}</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '6px 14px', background: '#2563eb', color: '#fff',
                    borderRadius: '6px', textDecoration: 'none', fontSize: '13px'
                  }}
                >
                  Open in New Tab ↗
                </a>
                <button
                  onClick={() => setPreviewUrl(null)}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}
                >✕</button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title="Certificate Preview"
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCertificates;
